/**
 * API Client Configuration
 *
 * This file configures the @hey-api/client-fetch client with:
 * - Base URL from environment
 * - Auth interceptor for JWT tokens
 * - Automatic token refresh
 *
 * Usage:
 *   import { configureApiClient } from '@/api/client';
 *   configureApiClient(); // Call once at app initialization
 *
 * The generated SDK functions will automatically use this client configuration.
 */

import { client } from './generated/client.gen';
import { tokenRefreshService } from '../utils/api/token-refresh.service';

/**
 * Get the API base URL from environment
 */
function getApiBaseUrl(): string {
  const appEnv = process.env.NEXT_PUBLIC_NODE_ENV;

  // Allow override via NEXT_PUBLIC_API_URL for ngrok or custom setups
  if (process.env.NEXT_PUBLIC_API_URL) {
    return process.env.NEXT_PUBLIC_API_URL;
  }

  if (appEnv === 'staging') {
    return 'https://api.lingualbox.jp';
  }

  if (appEnv === 'prod') {
    return 'https://api.lingualbox.com';
  }

  return 'http://api.lingualbox.loc:3000';
}

export const API_BASE_URL = getApiBaseUrl();

let isConfigured = false;

/**
 * Configure the @hey-api/client-fetch client
 * This should be called once when the app initializes
 */
export function configureApiClient() {
  if (isConfigured) {
    return;
  }

  // Configure base URL
  client.setConfig({
    baseUrl: API_BASE_URL,
  });

  // Add auth interceptor
  client.interceptors.request.use(async (request) => {
    const token = await tokenRefreshService.refreshTokenIfNeeded();
    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`);
    }
    return request;
  });

  isConfigured = true;
}

/**
 * Create fetch options with authentication
 * Use this for manual fetch calls that need auth
 */
export async function createAuthenticatedFetchOptions(
  options: RequestInit = {},
): Promise<RequestInit> {
  const token = await tokenRefreshService.refreshTokenIfNeeded();

  const headers = new Headers(options.headers);
  headers.set('Content-Type', 'application/json');

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  return {
    ...options,
    headers,
  };
}
